import React, { useMemo } from 'react'
import { useLoad } from '../../../../../../shared/hooks/load'
import { useApiBlobFileContent, useApiFormFileContent } from '../../../../../../api-new/state/storage'
import { useApiFormAssetThumbnail, useApiListAssetThumbnail } from '../../../../../../api-new/state/content'
import { UppyImageUpload } from '../../../../../../shared/image-upload/uppy-image-upload'
import { resolveThumbnailTarget } from './thumbnail-target'
import { thumbnailCache } from '../../../../../../shared/thumbnail-cache/thumbnail-cache'
import { useOrganizationID } from '../../../../../../shared/thumbnail-cache/use-thumbnail'
import { Card } from '../../../../../../shared/card/card'
import { THUMBNAIL_OUTPUT } from '../../../../../../shared/image-upload/image-upload-config'

export const THUMBNAIL_HINT = 'Shown at 2:1 on course cards and in the apps. Best at 1024×512 or larger.'
// Storage marks an uploaded thumbnail custom, and the Unity editor then
// stops replacing it with its own capture on publish.
export const THUMBNAIL_REPLACE_NOTE = 'Your picture replaces the one the Editor takes on publish.'
export const THUMBNAIL_REMOVE_BODY = "The thumbnail is deleted and cards show the placeholder. This can't be undone."

export const ThumnailAsset = ({ nodeID, onChange }) => {
  const apiListAssetThumbnail = useApiListAssetThumbnail()
  const apiFormAssetThumbnail = useApiFormAssetThumbnail()

  const apiFormFileContent = useApiFormFileContent()
  const apiBlobFileContent = useApiBlobFileContent()

  const organizationID = useOrganizationID()

  // The picture shown is the stored one: asked for once the node's
  // thumbnail row is known, and again only after an upload has succeeded
  // (handleUploaded). Not whenever the row changes: the first upload creates
  // its row before the image's PUT, and asking for that row's stream then
  // was a request for an empty file (a 500 from storage) that raced the
  // upload (dev request log, 2026-10-08).
  useLoad(
    async () => {
      let items
      try {
        items = await apiListAssetThumbnail.get(`${nodeID}/node`)
      } catch (e) {
        return
      }
      const current = items && items.length ? items[0] : null
      if (current) {
        await apiBlobFileContent.get(`${current.id}/stream`)
      } else {
        apiBlobFileContent.init()
      }
    },
    [nodeID]
  )

  let asset = useMemo(
    () => {
      if (apiListAssetThumbnail.items.length) {
        return apiListAssetThumbnail.items[0]
      } else {
        return null
      }
    },
    [apiListAssetThumbnail.items]
  )

  // The version row must exist before the bytes PUT (resolveThumbnailTarget
  // owns that ordering — unit-tested). The resolved asset is stashed so
  // onUploaded can refresh the right row and drop its stored copies (the
  // grids' cache, shared/thumbnail-cache) before they reload.
  const targetAssetRef = React.useRef(null)
  // The row this upload created (the node had no thumbnail yet), if any.
  const createdAssetRef = React.useRef(null)

  const resolveTarget = async () => {
    createdAssetRef.current = null
    const { target, url, method } = await resolveThumbnailTarget({
      asset,
      createAsset: () => apiListAssetThumbnail.post({ nodeID: nodeID, name: 'thumbnail' }),
      storageBase: process.env.REACT_APP_API_STORAGE,
    })
    targetAssetRef.current = target
    createdAssetRef.current = asset ? null : target
    return { url, method }
  }

  const handleUploaded = async () => {
    const target = targetAssetRef.current
    createdAssetRef.current = null
    if (!target) return

    await thumbnailCache.invalidate(organizationID, target.id)
    await apiBlobFileContent.get(`${target.id}/stream`)
    onChange && onChange()
  }

  // The upload failed (UppyImageUpload says why): the picture shown stays
  // as it was. A row this upload created holds nothing, so it goes again --
  // storage's files by id (in case the bytes did land), then content's row
  // -- and the node is left as it was, not with an empty thumbnail every
  // page then asks storage for. An existing thumbnail is never touched. If
  // taking the row away fails too, it stays; the next upload reuses it.
  const handleUploadFailed = async () => {
    const created = createdAssetRef.current
    createdAssetRef.current = null
    if (!created) return

    try {
      await apiFormFileContent.delete(created.id)
      await apiFormAssetThumbnail.delete(`${created.id}`)
    } catch (err) {
      return
    }
    apiListAssetThumbnail.remove(created)
  }

  // Storage's DELETE /file-content/:id takes the thumbnail's id (its files
  // and versions go); then content's row. Either way no stored copy of it is
  // shown again. A failure is thrown on: the uploader's Remove dialog says
  // so, with its reference, and stays open.
  const handleDelete = async () => {
    let failed = null
    try {
      await apiFormFileContent.delete(asset.id)
      await apiFormAssetThumbnail.delete(`${asset.id}`)
    } catch (err) {
      failed = err || {}
    }

    await thumbnailCache.invalidate(organizationID, asset.id)

    if (failed) throw failed

    apiListAssetThumbnail.init()
    apiBlobFileContent.init()

    onChange && onChange()
  }

  // Its own card, apart from the page's form: the thumbnail saves on its own
  // (Save thumbnail), never with the form's Update.
  return (
    <Card title="Thumbnail">
      <UppyImageUpload
        previewBlob={apiBlobFileContent.blob}
        aspect={[2, 1]}
        output={THUMBNAIL_OUTPUT}
        maxFileSize={500 * 1024}
        noun="thumbnail"
        hint={THUMBNAIL_HINT}
        replaceNote={THUMBNAIL_REPLACE_NOTE}
        removeBody={THUMBNAIL_REMOVE_BODY}
        frameClassName="course-image"
        resolveTarget={resolveTarget}
        onUploaded={handleUploaded}
        onUploadFailed={handleUploadFailed}
        onDelete={asset ? handleDelete : undefined}
      />
    </Card>
  )
}
